import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, FileText, Map, ShoppingCart, Flame, AlertTriangle } from 'lucide-react';
import { T, glassLight, useIsMobile } from '../ui';
import { useGasData } from './gasData';
import type { GasView } from '../types';
import { Clientes } from './clientes';
import { Contratos } from './Contratos';
import { Rutas } from './Rutas';
import { Ventas } from './Ventas';
const NAV: {
  id: GasView;
  label: string;
  icono: React.ElementType;
}[] = [{
  id: 'clientes',
  label: 'Clientes',
  icono: Users
}, {
  id: 'contratos',
  label: 'Contratos',
  icono: FileText
}, {
  id: 'rutas',
  label: 'Rutas',
  icono: Map
}, {
  id: 'ventas',
  label: 'Ventas',
  icono: ShoppingCart
}];
function fmtRD(n: number) {
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    maximumFractionDigits: 0
  }).format(n);
}
export default function GasApp() {
  const isMobile = useIsMobile();
  const [view, setView] = useState<GasView>('clientes');
  const data = useGasData();
  const stats = useMemo(() => {
    const hoy = new Date();
    const ventasMes = data.ventas.filter(v => {
      const f = new Date(v.fecha);
      return f.getMonth() === hoy.getMonth() && f.getFullYear() === hoy.getFullYear();
    });
    return {
      clientesActivos: data.clientes.filter(c => c.estado === 'activo').length,
      contratosVigentes: data.contratos.filter(c => c.estado === 'vigente').length,
      rutasActivas: data.rutas.filter(r => r.estado === 'activa').length,
      totalMes: ventasMes.reduce((acc, v) => acc + (v.total || 0), 0)
    };
  }, [data.clientes, data.contratos, data.rutas, data.ventas]);

  // Conteo por pestaña: saber cuántos registros hay detrás de cada una antes
  // de entrar ahorra un clic.
  const conteos: Record<GasView, number> = {
    clientes: data.clientes.length,
    contratos: data.contratos.length,
    rutas: data.rutas.length,
    ventas: data.ventas.length
  };
  const pages: Record<GasView, React.ReactNode> = {
    clientes: <Clientes clientes={data.clientes} onAddCliente={data.onAddCliente} onDeleteCliente={data.onDeleteCliente} onUpdateCliente={data.onUpdateCliente} data-id="element-776" data-mp="155o3lmlwng5n" />,
    contratos: <Contratos contratos={data.contratos} clientes={data.clientes} onAddContrato={data.onAddContrato} onDeleteContrato={data.onDeleteContrato} onUpdateContrato={data.onUpdateContrato} data-id="element-777" data-mp="1iyngrhy7sya1" />,
    rutas: <Rutas rutas={data.rutas} clientes={data.clientes} onAddRuta={data.onAddRuta} onDeleteRuta={data.onDeleteRuta} onUpdateRuta={data.onUpdateRuta} data-id="element-778" data-mp="1stp5wdshq0cm" />,
    ventas: <Ventas ventas={data.ventas} clientes={data.clientes} onAddVenta={data.onAddVenta} onDeleteVenta={data.onDeleteVenta} onUpdateVenta={data.onUpdateVenta} data-id="element-779" data-mp="21wgkunkkre90" />
  };
  return <div data-id="element-780" data-mp="1xsikyanfdzew">
      <style data-id="element-781" data-mp="7hxiqm3y3cri">{`
        .gas-tab { position: relative; z-index: 1; }
        .gas-tab:hover { background: ${T.surfaceHover}; }
      `}</style>

      {/* ── ENCABEZADO ────────────────────────────────────────────────
       Antes era un bloque oscuro con degradados: destacaba, pero era el
       único trozo oscuro de toda la app y parecía de otro sistema. Ahora
       usa la misma paleta clara y las mismas tarjetas de indicador que
       Pedidos, Auditoría y Sucursales. */}
      <motion.div initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.35,
      ease: [0.2, 0.8, 0.2, 1]
    }} style={{
      marginBottom: 22
    }} data-id="element-782" data-mp="3fpvosu0qak94">

        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        marginBottom: 12
      }} data-id="element-783" data-mp="3asn7swrbgm9g">

          <div style={{
          width: 34,
          height: 34,
          borderRadius: 11,
          flexShrink: 0,
          background: 'linear-gradient(135deg, #FF8C00 0%, #FF5A00 100%)',
          boxShadow: '0 5px 16px rgba(255,107,1,.32)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} data-id="element-784" data-mp="2wu5m14kz1ju6">

            <Flame size={17} color="#fff" strokeWidth={2.4} data-id="element-785" data-mp="2w3yq2bnw5jhi" />
          </div>
          <div style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: T.accent
        }} data-id="element-786" data-mp="2w1w360i1c6h0">

            Gestión comercial
          </div>
        </div>

        <h1 style={{
        fontSize: isMobile ? 27 : 34,
        fontWeight: 800,
        letterSpacing: '-0.03em',
        color: T.text,
        margin: '0 0 6px'
      }} data-id="element-787" data-mp="2i4z2z8g11cng">

          Gas Industrial
        </h1>
        <p style={{
        fontSize: 13.5,
        color: T.textSecondary,
        margin: 0,
        maxWidth: 560
      }} data-id="element-788" data-mp="28dcsxfffdjma">

          Clientes, contratos, rutas de distribución y ventas, todo en un solo
          lugar.
        </p>
      </motion.div>

      {/* Resumen transversal. Va en una sola tira compacta, no en tarjetas
       grandes, porque cada pestaña ya trae sus propios indicadores: dos
       filas de tarjetas seguidas competían entre sí. */}
      <div style={{
      ...glassLight,
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))',
      gap: 0,
      padding: 0,
      marginBottom: 22,
      overflow: 'hidden'
    }} data-id="element-789" data-mp="4xresefbzmay">

        {[{
        label: 'Clientes activos',
        valor: String(stats.clientesActivos),
        color: T.accent
      }, {
        label: 'Contratos vigentes',
        valor: String(stats.contratosVigentes),
        color: T.ok
      }, {
        label: 'Rutas activas',
        valor: String(stats.rutasActivas),
        color: T.info
      }, {
        label: 'Ventas del mes',
        valor: fmtRD(stats.totalMes),
        color: T.warn
      }].map((m, i) => <div key={m.label} style={{
        padding: isMobile ? '14px 16px' : '16px 20px',
        borderLeft: !isMobile && i > 0 ? `1px solid ${T.border}` : undefined,
        borderTop: isMobile && i > 1 ? `1px solid ${T.border}` : undefined,
        borderLeftWidth: isMobile && i % 2 ? 1 : undefined,
        borderLeftStyle: isMobile && i % 2 ? 'solid' : undefined,
        borderLeftColor: isMobile && i % 2 ? T.border : undefined
      }} data-id="element-790" data-mp="3w3sj844sjhnm" data-mpi={i}>

            <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '.09em',
          textTransform: 'uppercase',
          color: T.textMuted,
          marginBottom: 7
        }} data-id="element-791" data-mp="5t50d3jde76c">

              <span style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: m.color,
            flexShrink: 0
          }} data-id="element-792" data-mp="3o0cc3dymdwaq" />

              {m.label}
            </div>
            <div style={{
          fontSize: isMobile ? 19 : 23,
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: T.text
        }} data-id="element-793" data-mp="w4qehjegk9y">

              {m.valor}
            </div>
          </div>)}
      </div>

      {data.sinTablas ? <div style={{
      display: 'flex',
      gap: 11,
      alignItems: 'flex-start',
      padding: '13px 16px',
      marginBottom: 20,
      borderRadius: T.rMd,
      background: T.warnSoft,
      border: `1px solid ${T.warn}33`,
      color: T.warn,
      fontSize: 12.5,
      lineHeight: 1.5
    }} data-id="element-794" data-mp="17boz7pj7x0np">

          <AlertTriangle size={16} style={{
        flexShrink: 0,
        marginTop: 1
      }} data-id="element-795" data-mp="15urvhxzxu1e4" />
          <span data-id="element-796" data-mp="1atw9r31pypbb">
            <strong data-id="element-797" data-mp="1atcxa1uu8t4o">Solo guardando en este navegador.</strong> Las tablas de
            Gas Industrial todavía no existen en Supabase, así que estos
            registros no se están sincronizando. Corre el script de creación de
            tablas en el SQL Editor de tu proyecto.
          </span>
        </div> : null}

      {/* ── PESTAÑAS ── */}
      <div style={{
      display: 'flex',
      gap: 7,
      marginBottom: 24,
      overflowX: 'auto',
      paddingBottom: 2
    }} data-id="element-798" data-mp="1trfkcb3tsfzu">

        {NAV.map(item => {
        const Icono = item.icono;
        const activa = view === item.id;
        return <button key={item.id} type="button" className="gas-tab" onClick={() => setView(item.id)} style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          borderRadius: 99,
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          color: activa ? T.accent : T.textSecondary,
          background: activa ? T.accentSoft : 'transparent',
          border: `1px solid ${activa ? T.accentBorder : T.border}`,
          transition: 'color .15s, border-color .15s'
        }} data-id="element-799" data-mp="2m9kudnri3c8y">

              <Icono size={15} data-id="element-800" data-mp="w2vd6dnu1saj" />
              {item.label}
              <span style={{
            fontSize: 11,
            fontWeight: 800,
            padding: '1px 7px',
            borderRadius: 99,
            background: activa ? 'rgba(255,255,255,.7)' : T.surfaceSubtle,
            color: activa ? T.accent : T.textMuted
          }} data-id="element-801" data-mp="r4tv9w257z0n">

                {conteos[item.id]}
              </span>
            </button>;
      })}
      </div>

      {/* ── CONTENIDO ── */}
      <div style={{
      ...glassLight,
      padding: isMobile ? 16 : 22
    }} data-id="element-802" data-mp="1wbv1uj9piyxa">
        <AnimatePresence mode="wait" data-id="element-803" data-mp="1w9qsvmwyb9q5">
          <motion.div key={view} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.2,
          ease: [0.2, 0.8, 0.2, 1]
        }} data-id="element-804" data-mp="20dgec3ibxb6y">

            {pages[view]}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}